import { Vue } from 'vue-property-decorator';
import {
    DataTableHeaders, DetailApi,
    DetailConfig, DetailLoaders,
    GridConfig,
    GridDataOptions,
    GridFilters,
    GridParams,
    GridSorters,
} from '@/types/index';
import { DataPagination } from 'vuetify';

/**
 * v-form
 * @see https://vuetifyjs.com/en/api/v-form/
 */
export interface VForm extends Vue {
    validate(): boolean;
    reset(): void;
    resetValidation(): void;
}

/**
 * v-dialog
 * @see https://vuetifyjs.com/en/api/v-dialog/
 */
export interface VDialog extends Vue {
    show(): void;
}

/**
 * Eigene Komponente welche zurückgesetzt werden kann
 */
export interface Resettable extends Vue {
    reset(): void;
}

/**
 * v-curreny-field
 * @see https://phiny1.github.io/v-currency-field/config.html
 */
export interface VCurrencyField extends Vue {
    setValue(value: number): void;
}

/**
 * v-select
 * @see https://vuetifyjs.com/en/api/v-select/
 */
export interface VSelect extends Vue {
    blur(): void;
    activateMenu(): void;
}

/**
 * Mixin
 * @see VTextField
 * @see VAutocomplete
 */
export interface Validatable extends Vue {
    errorBucket: string[];
    hasColor: boolean;
    hasFocused: boolean;
    hasInput: boolean;
    isFocused: boolean;
    isResetting: boolean;
    valid: boolean;

    reset(): void;
    resetValidation(): void;
    validate(): boolean;
}

/**
 * v-text-field
 * @see https://vuetifyjs.com/en/api/v-text-field/
 */
export interface VTextField extends Validatable {
    focus(): void;
    blur(): void;
}

/**
 * v-autocomplete
 * @see https://vuetifyjs.com/en/api/v-autocomplete/
 */
export interface VAutocomplete extends Validatable {
    blur(): void;
    activateMenu(): void;
    focus(): void;
}

/**
 * v-menu
 * @see https://vuetifyjs.com/en/api/v-menu/
 */
export interface VMenu extends Vue {
    save(value: unknown): void;
}

/**
 * v-select item
 * @typeparam <V> Value type
 * @typeparam <T> Text type
 * @see https://vuetifyjs.com/en/api/v-select/
 */
export interface ListItem<V = string | number, T = string> {
    value?: V;
    text?: T;
    disabled?: boolean;
    divider?: boolean;
    header?: string;
}

/**
 * v-select item list
 * @typeparam <V> Value type
 * @typeparam <T> Text type
 * @see https://vuetifyjs.com/en/api/v-select/
 */
export type ListItems<V = string | number, T = string> = ReadonlyArray<ListItem<V, T>>;

/**
 * v-autocomplete filter function factory util
 * @typeparam <T> Item type
 * @param keys Properties to compare to
 */
export const createAutocompleteFilter = <T>(...keys: (keyof T)[]) => {
    return (item: T, queryText: string): boolean => {
        return keys.some((key) => {
            return ((item[key] as { toString(): string })
                .toString()
                .toLocaleLowerCase()
                .indexOf(queryText.toLocaleLowerCase()) ?? 0) > -1;
        });
    };
};

export interface VDatePickerInput extends Vue {
    resetValidation(): void;
    validate(): boolean;
}

/**
 * v-grid
 * @typeparam <T> DB-Objekt
 */
export interface VGrid<T> extends Vue {
    config: GridConfig<T>;
    filters: GridFilters<T>;
    itemsPerPageOptions: number[];
    loadDebounceDelay: number;
    $refs: { table: Vue; };
    saveGrid: (payload: { key: string, data: GridParams<T> }) => void;
    loadGrid: (payload: { key: string }) => Promise<GridParams<T>>;
    options: GridDataOptions<T>;
    pagination: DataPagination;
    items: (T | Partial<T>)[];
    loading: boolean;
    loadingManualRefresh: boolean;
    totalCount: number;
    get headers(): DataTableHeaders<T>;
    get sorters(): GridSorters<T>;
    createParams(): GridParams<T>;
    beforeMount(): void;
    load(filterReset?: boolean): void;
}

/**
 * v-detail
 * @typeparam <T> DB-Objekt
 */
export interface VDetail<T, S = Record<string, unknown>> extends Vue {
    config: DetailConfig<T>;
    params: Record<string, unknown>;
    $refs: { dialog: VDialog; };
    data: T | Partial<T>;
    selections: S;
    permissions: string[];
    loaders: DetailLoaders;
    get title(): string;
    get remoteApi(): DetailApi;
}
